<script>
import axios from 'axios'
import Raven from 'raven-js'
import { mapGetters, mapState } from 'vuex'

import Layout from '@layouts/main'
import ErrorComponent from '@components/error-component'
import commomUtils from '@mixins/common-utils.js'
import deepLink from '@mixins/deeplink.js'
import ENV from '@src/env'
import { widgetActions } from '@state/helpers'
import GTM_CONSTANTS from '@constants/google-tag-manager'
import AMPLITUDE from '@constants/amplitude'
import { getScreenWidgets } from './utils'

export default {
  page: {
    title: 'Widgets',
    meta: [{ name: 'description', content: 'Widgets' }],
  },
  components: {
    Layout,
    ErrorComponent,
  },
  mixins: [commomUtils(), deepLink()],
  data() {
    return {
      loading: false,
      error: null,
      componentList: [],
      params: {},
      userCountry: '',
      isUserRT: false
    }
  },
  computed: {
    ...mapGetters('widgets', ['userEnv', 'logsUserID', 'screenBuilderBody']),
    ...mapState('zendesk', {
      fetching: state => state.fetching,
    }),
    ...mapState('widgets', {
      common: state => state.common,
    }),
    viewEvent() {
      return `VIEW_${this.common.params.screenId}`
    },
    userForLogs() {
      let { user } = this.common

      user = Object.assign({}, user, {
        userLogsId: this.logsUserID,
      })
      delete user.token
      return user
    },
    payloadForlogs() {
      const payload = Object.assign({}, this.$route.params, this.$route.query)
      delete payload.token
      delete payload.store_type
      delete payload.enable_webkit
      delete payload.dev

      return payload
    },
    requestScreenUrl() {
      const { order_id } = this.$route.query // eslint-disable-line
      const orderExist = order_id && Number(order_id) > 1000 // eslint-disable-line

      return orderExist
        ? '/api/support-flows/screens/build-screen'
        : '/api/support-flows/screens/build-screen-no-order'
    },
  },
  watch: {
    $route: 'fetchScreen',
  },
  beforeCreate() {
    const validLAnguages = ['es', 'en', 'pt']
    const { language: queryLanguage } = this.$route.query
    const browserLanguage =
      window.navigator.userLanguage || window.navigator.language
    const language = queryLanguage || browserLanguage
    const [matchLanguage] = validLAnguages.filter(l =>
      language.toLowerCase().includes(l)
    )
    const lang = matchLanguage || browserLanguage

    this.$root._i18n._vm.locale = lang
    axios.defaults.headers.post['Accept-Language'] = lang
  },
  created() {
    try {
      // report entrance for g-analytics
      this.$ga.page(this.$router)
      this.initStore()
      const { screenId } = this.$route.params

      this.params = Object.assign({}, this.$route.query, {
        screen_id: screenId,
      })

      this.validateParams(this.params)
      this.fetchScreen()
    } catch (err) {
      this.error = err.toString()
    }
  },
  methods: {
    ...widgetActions,
    initStore() {
      const { country_code: countryCode, token } = this.$route.query
      this.commitUser({
        countryCode,
        token,
        os: this.getMobileOperatingSystem(),
      })
      this.commitParams(this.$route.params)
      this.commitQuery(this.$route.query)
    },
    async fetchScreen() {
      this.loading = true

      try {
        const { data } = await axios.post(
          `${ENV.rappi.url[this.userEnv]}${this.requestScreenUrl}`,
          this.screenBuilderBody,
          {
            headers: { Authorization: `Bearer ${this.params.token}` },
          }
        )
        this.loading = false

        const { screenWidgets } = data.data
        this.componentList = getScreenWidgets(screenWidgets)
        this.componentList.map(c => this.commitWidgetData(c))
        this.dispatchEventTrack(this.viewEvent)
      } catch (err) {
        this.loading = false

        Raven.captureException(err)
        const { status } = err.response
        this.dispatchEventTrack(
          AMPLITUDE.SERVER_ERROR,
          JSON.parse(JSON.stringify(err))
        )

        if (status >= 500 && status < 600) this.callDeepLink('goto=home')
        else this.error = err.toString()
        this.$noty.error(this.error, {
          timeout: 3000,
          layout: 'topCenter',
          progressBar: false,
          modal: true,
          theme: 'relax',
        })
      }
    },
    getComponent({ component }) {
      return component()
    },
    handleChildChange(childData) {
      this.loading = childData.fetching
    },
    dispatchEventTrack(eventName, payload = this.payloadForlogs) {
      this.$gtm.trackEvent({
        event: GTM_CONSTANTS.LOG_EVENT,
        noninteraction: eventName,
        payload,
        eventType: eventName,
        user: this.userForLogs,
      })
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.container">
      <div
        v-if="loading || fetching"
        :class="$style.container__loading">
        <v-progress-circular
          :width="3"
          :class="[ isUserRT && { [$style.progressRT]: isUserRT }, $style.progress]"
          indeterminate
        />
      </div>

      <div
        v-else-if="error"
        :class="$style.container__error">
        <ErrorComponent :text="$t('COMMON.ERROR_MESSAGE')"/>
      </div>

      <div v-else>
        <div
          v-for="component in componentList"
          :key="component.key">
          <component
            :is="getComponent(component)"
            :class="$style.component"
            :token="params.token"
            :component-data="JSON.parse(JSON.stringify(component.data))"
            :user-country="userEnv"
            @onChildChange="handleChildChange"
          />
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped lang="scss" module>
@import '@design';
.container {
  &__loading {
    height: 100vh;
    padding-top: 10rem;
    color: $primary-green;
  }

  &__error {
    height: 100vh;
    padding-top: 10rem;
  }

  &__spacer {
    &Small {
      height: 1px;
    }

    &Medium {
      height: 6rem;
    }

    &Big {
      height: 1px;
    }
  }
}
.component {
  width: auto;
  // padding: 0.5rem 0 0.5rem 0;

  &:not(:last-child) {
    margin-bottom: 1rem;
  }

  &:first-child {
    margin-top: 1rem;
  }

  &:only-child {
    // margin-top: 0;
  }
}

.btnContainer {
  width: 100%;
  margin-bottom: 2rem;

  > button {
    &:focus {
      outline: 0;
    }
  }
}

.progress {
  color: $rappi-green;

  &RT {
    color: $color-rt-cian-primary;
  }
}
</style>
